<template>
  <PreviewFrame :accent="slide.accent">
    <template #presenter>
      <div class="tt-header">
        <span class="tt-bar tt-bar--xl"></span>
        <span class="tt-bar tt-bar--md"></span>
      </div>
      <div class="tt-statements">
        <div
          v-for="(s, i) in statements"
          :key="i"
          class="tt-stmt"
          :class="{ '--leading': i === leadIndex }"
          :style="{ '--i': i }"
        >
          <span class="tt-num">{{ i + 1 }}</span>
          <div class="tt-stmt-body">
            <span class="tt-bar tt-bar--lg"></span>
            <div class="tt-track">
              <span class="tt-fill" :style="{ '--pct': s.pct + '%', '--row-i': i }"></span>
            </div>
          </div>
          <span class="tt-pct">{{ s.pct }}%</span>
        </div>
      </div>
    </template>

    <template #audience>
      <span class="tt-phone-q"></span>
      <div class="tt-phone-hint">
        Spot the lie:
      </div>
      <button
        v-for="(s, i) in statements"
        :key="i"
        type="button"
        class="tt-phone-opt"
        :class="{ '--tapped': i === tapIndex }"
        :style="{ '--i': i }"
        tabindex="-1"
      >
        <span class="tt-phone-num">{{ i + 1 }}</span>
        <span class="tt-bar tt-bar--md"></span>
        <span v-if="i === tapIndex" class="material-symbols-rounded tt-phone-icon">close</span>
      </button>
    </template>
  </PreviewFrame>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue'
import type { SlideType } from '../../data/slideTypeCatalog'
import PreviewFrame from './PreviewFrame.vue'

export default Vue.extend({
  name: 'PreviewTwoTruthsLie',
  components: { PreviewFrame },
  props: { slide: { type: Object as PropType<SlideType>, required: true } },
  data() {
    return {
      statements: [
        { pct: 18 },
        { pct: 64 },
        { pct: 18 },
      ],
      leadIndex: 1,
      tapIndex: 1,
    }
  },
})
</script>

<style scoped lang="scss">
.tt-bar {
  display: block; height: 7px; border-radius: 999px;
  background: rgba(15, 15, 20, 0.1);
}
.tt-bar--xl { width: 70%; background: rgba(15, 15, 20, 0.2); height: 8px; }
.tt-bar--lg { width: 75%; }
.tt-bar--md { width: 65%; }

.tt-header {
  display: flex; flex-direction: column; gap: 6px;
  padding-bottom: 8px;
  border-bottom: 1px dashed color-mix(in srgb, var(--accent) 20%, transparent);
}

.tt-statements { display: grid; gap: 8px; }
.tt-stmt {
  display: grid;
  grid-template-columns: 22px 1fr auto;
  align-items: center;
  gap: 8px;
  padding: 7px 9px;
  border-radius: 8px;
  background: rgba(15, 15, 20, 0.03);
  border: 1px solid rgba(15, 15, 20, 0.06);
}
.tt-num {
  width: 22px; height: 22px;
  border-radius: 999px;
  background: rgba(15, 15, 20, 0.06);
  color: rgba(15, 15, 20, 0.55);
  font-size: 11px;
  font-weight: 700;
  display: grid; place-items: center;
}
.tt-stmt-body {
  display: flex; flex-direction: column; gap: 5px;
  min-width: 0;
}
.tt-track {
  height: 5px;
  border-radius: 999px;
  background: rgba(15, 15, 20, 0.06);
  overflow: hidden;
}
.tt-fill {
  display: block;
  height: 100%; width: 0;
  border-radius: 999px;
  background: var(--accent);
  animation: tt-grow 4s cubic-bezier(0.23, 1, 0.32, 1) infinite;
  animation-delay: calc(var(--row-i) * 100ms + 600ms);
}
.tt-pct {
  font-size: 10px; font-weight: 700;
  color: rgba(15, 15, 20, 0.55);
  font-variant-numeric: tabular-nums;
}
.tt-stmt.--leading {
  background: color-mix(in srgb, var(--accent) 10%, white);
  border-color: color-mix(in srgb, var(--accent) 35%, transparent);
}
.tt-stmt.--leading .tt-num { background: var(--accent); color: white; }
.tt-stmt.--leading .tt-pct { color: var(--accent); }

/* phone */
.tt-phone-q {
  display: block; height: 8px; width: 70%;
  border-radius: 999px;
  background: rgba(15, 15, 20, 0.18);
  margin-bottom: 2px;
}
.tt-phone-hint {
  font-size: 10px; font-weight: 700;
  color: rgba(15, 15, 20, 0.55);
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-bottom: 2px;
}
.tt-phone-opt {
  appearance: none; background: white;
  display: grid;
  grid-template-columns: 20px 1fr auto;
  align-items: center;
  gap: 8px;
  padding: 7px 9px;
  border-radius: 8px;
  border: 1px solid rgba(15, 15, 20, 0.08);
  cursor: pointer;
  font-family: inherit;
  text-align: left;
}
.tt-phone-num {
  width: 20px; height: 20px;
  border-radius: 999px;
  background: rgba(15, 15, 20, 0.06);
  color: rgba(15, 15, 20, 0.55);
  font-size: 10px;
  font-weight: 700;
  display: grid; place-items: center;
}
.tt-phone-opt.--tapped {
  border-color: color-mix(in srgb, var(--accent) 60%, transparent);
  background: color-mix(in srgb, var(--accent) 10%, white);
  animation: tt-tap 4s cubic-bezier(0.23, 1, 0.32, 1) infinite;
}
.tt-phone-opt.--tapped .tt-phone-num { background: var(--accent); color: white; }
.tt-phone-icon { font-size: 14px; color: var(--accent); }

@keyframes tt-grow {
  0%, 12%   { width: 0; }
  60%       { width: var(--pct, 50%); }
  92%, 100% { width: var(--pct, 50%); }
}
@keyframes tt-tap {
  0%, 4%   { box-shadow: 0 0 0 0 color-mix(in srgb, var(--accent) 35%, transparent); }
  18%      { box-shadow: 0 0 0 8px color-mix(in srgb, var(--accent) 0%, transparent); }
  100%     { box-shadow: 0 0 0 0 color-mix(in srgb, var(--accent) 0%, transparent); }
}

@media (prefers-reduced-motion: reduce) {
  .tt-fill { animation: none; width: var(--pct, 50%); }
  .tt-phone-opt.--tapped { animation: none; }
}
</style>
